<script lang="ts">
    import {CurrentAuthService} from 'shared/services';
    import {ErpSidebarNavService} from 'src/features/erp/components';
    import type {Auth} from 'src/typings';
    import {getInjector, getLocalization} from 'src/core';
    import type {CssSize} from 'shared/ui-widgets/types';
    import {cleanup, exists} from 'shared/utils/custom-utils';
    import {fly} from 'svelte/transition';
    import {onDestroy, onMount} from 'svelte';
    import LoginService from 'shared/login/login.service';
    import {ErpBlurredIconsBackground} from 'src/features/erp/components/index';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import InfoBanner from 'shared/ui-widgets/banner/InfoBanner.svelte';
    import KpHeading from 'shared/components/kp-heading/KpHeading.svelte';

    const currentAuthService = getInjector().getByClass(CurrentAuthService);
    const loginService = getInjector().getByClass(LoginService);
    const erpSidebarNavService = getInjector().getByClass(ErpSidebarNavService);
    const localize = getLocalization();

    export let pageClass: string;
    export let loading = false;
    export let loadError = false;
    export let gap: CssSize = '32px';
    export let withoutContentPadding = false;
    export let showHeadingWhenNotLoggedIn = false;
    export let dontCollapseSidebar = false;
    export let withoutLogo = false;

    const pageFlyInParams = {y: 15, duration: 250};

    let auth: Auth;
    const currentAuthSubscription = currentAuthService.currentAuth$().subscribe((currentAuth) => auth = currentAuth);

    onMount(() => {
        if (dontCollapseSidebar) {
            return;
        }

        erpSidebarNavService.toggleCollapse(true);
    });

    onDestroy(() => {
        cleanup(currentAuthSubscription);
    });
</script>

{#if loading}
    <div class="loading-container" in:fly={pageFlyInParams}>
        <KpLoadingBlock/>
    </div>
{/if}

{#if loadError}
    <div class="loading-container load-error" in:fly={pageFlyInParams}>
        <IconedContent align="center"
                       iconColor="var(--danger-red)"
                       justify="center"
                       orientation="vertical"
                       icon="exclamation">

            Nastala chyba při načítání!
        </IconedContent>
    </div>
{/if}

{#if !loading && !loadError}
    {#key auth.evided}
        <div class="erp-page-layout {pageClass}-container" in:fly={pageFlyInParams}>
            {#if exists($$slots.heading) && (auth.evided || showHeadingWhenNotLoggedIn)}
                <slot name="heading" {auth}/>
            {/if}

            <div class="page-layout-content {pageClass}"
                 class:content-padded={!withoutContentPadding || !auth.evided}
                 class:top-padded={(exists($$slots.heading) && !withoutContentPadding) || !auth.evided}
                 style:--gap={gap}>

                {#if auth.evided}
                    <slot {auth}/>
                {:else}
                    {#if exists($$slots['no-auth-content'])}
                        <slot name="no-auth-content"/>
                    {/if}

                    {#if !exists($$slots['no-auth-content'])}
                        <div class="info-banner-container">
                            {#if !withoutLogo}
                                <img class="sutin-logo" src="/erp/assets/logo.svg" alt="Sutin 2 logo"/>
                                <ErpBlurredIconsBackground/>

                                <div class="heading-texts-container">
                                    <KpHeading type="h1">
                                        Přihlašte se do systému, prosím
                                    </KpHeading>
                                </div>
                            {/if}

                            <div class="info-banner">
                                <InfoBanner>
                                    <svelte:fragment slot="text">
                                        Nejste přihlášeni. Pro odemčení funkcionalit systému se přihlaste, prosím.
                                    </svelte:fragment>

                                    <KpButton on:click={() => loginService.login()}>
                                        <IconedContent icon="sign-in-alt">
                                            {localize(/* @kp-localization login.loginButton */ 'login.loginButton')}
                                        </IconedContent>
                                    </KpButton>
                                </InfoBanner>
                            </div>
                        </div>
                    {/if}
                {/if}
            </div>
        </div>
    {/key}
{/if}

<style lang="less">
    @import (reference) "styles/portaro-erp.less";

    .loading-container {
        .flex-grow();
        align-items: center;
        justify-content: center;
    }

    .erp-page-layout {
        .flex-grow();
        position: relative;

        .page-layout-content {
            .flex-grow();
            gap: var(--gap);

            &.content-padded {
                padding: 0 @main-padding-horizontal @main-padding-vertical;
            }

            &.top-padded {
                padding-top: @main-padding-vertical;
            }

            &.full-padded {
                padding: @main-padding-horizontal @main-padding-vertical;
            }

            .info-banner-container {
                .flex-grow();
                align-items: center;
                justify-content: center;
                isolation: isolate;
                gap: @spacing-xxl;

                .sutin-logo {
                    height: 100px;
                }

                .heading-texts-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                    gap: @spacing-s;
                }

                .info-banner {
                    width: 760px;
                    max-width: 80vw;
                }
            }
        }
    }

    :global {
        .erp-page-layout .erp-tabbed-subpages-container {
            .record-grid-tab-page {
                .kp-record-grid-container {
                    .flex-grow();
                    .record-grid-display-flex-grow();
                }

                .record-grid-display-flex-grow();
            }
        }
    }
</style>